import { Material } from '@/app/entities/Material'
import { withCdn } from '@/app/utils/withCdn'
import { Button } from '@/view/components/ui/button'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { AlertTriangle } from 'lucide-react'
import { useMemo } from 'react'
import { getMaterialImages, getMaterialProps } from '../MaterialPage/helper'
import { ImageTypeSelect } from './ImageTypeSelect'
import { cn } from '@/app/utils/cn'
import { PARTNER_LOGO } from '@/app/utils/getPartnerLogo'

interface SelectedMaterialProps {
  material: Material | null
}
export type ImageOption = 'feedImages' | 'storyImages' | 'all'
type OptionType = {
  value: ImageOption
  label: string
  description: string
  quantity: number
  disabled: boolean
}

const getOptions = (material: Material) => {
  const options: OptionType[] = []
  const materialProps = getMaterialProps(material!)

  options.push({
    value: 'feedImages',
    label: 'Apenas Feed',
    description: '1080 x 1080',
    quantity: material.feedImages.length,
    disabled: !materialProps.hasFeed,
  })

  options.push({
    value: 'storyImages',
    label: 'Apenas Stories',
    description: '1080 x 1920',
    quantity: material.storyImages.length,
    disabled: !materialProps.hasStory,
  })

  //   options.push({
  //     value: 'youtubeImages',
  //     label: 'Youtube',
  //     description: '1080 x 1080',
  //     quantity: material.youtubeImages.length,
  //     disabled: !materialProps.hasYoutube,
  //   })
  //   defaultOption = 'youtubeImages'

  return { options }
}

export function SelectedMaterial({ material }: SelectedMaterialProps) {
  const { options } = useMemo(() => getOptions(material!), [material])

  const currentImages = useMemo(() => {
    if (!material) return []

    return getMaterialImages(material)
  }, [material])

  const image = currentImages?.find((img) =>
    ['png', 'jpg', 'jpeg'].includes(img.split('.').pop() as string),
  )

  const handleDownload = async (imageType: ImageOption) => {
    if (!currentImages || currentImages.length === 0 || !material) return

    const folderNameMap = {
      feedImages: 'Feed',
      storyImages: 'Story',
    }

    // Função auxiliar para baixar uma imagem
    const downloadImage = async (
      image: string,
      folderName: string,
      index: number,
    ) => {
      try {
        const response = await fetch(withCdn(image))
        if (!response.ok) throw new Error(`Erro HTTP ${response.status}`)
        const blob = await response.blob()
        const extension = image.split('.').pop() || 'jpg'
        const fileName = `${folderName}_${material.name}${index > 0 ? `(${index})` : ''}.${extension}`

        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(a.href)
      } catch (error) {
        console.error(`Erro ao processar a imagem ${image}:`, error)
      }
    }

    if (imageType !== 'all') {
      const folderName = folderNameMap[imageType] || 'Image'
      await Promise.allSettled(
        material[imageType].map((image, index) =>
          downloadImage(image, folderName, index),
        ),
      )
    } else {
      const imageCategories = ['feedImages', 'storyImages'] as const
      await Promise.allSettled(
        imageCategories.flatMap((type) =>
          material[type].map((image, index) =>
            downloadImage(image, folderNameMap[type] || type, index),
          ),
        ),
      )
    }
  }

  if (!material) return null

  return (
    <div className="mb-10 flex max-w-[428px] flex-col gap-4">
      <img
        className={cn(
          'w-[428px] rounded-md object-cover',
          !image && 'rounded-2xl opacity-80',
        )}
        src={image ? withCdn(image) : PARTNER_LOGO}
        alt={material.name}
      />
      <div className="flex flex-col gap-1.5">
        <p className="font-semibold capitalize">{material.name}</p>
        <span className="text-xs  capitalize text-black04">
          {format(new Date(material.dateLiberation), 'dd/MM/yyyy | eeee', {
            locale: ptBR,
          })}
        </span>
        <p className="text-justify text-xs">{material.description}</p>
      </div>
      <div className="flex w-full items-start justify-start gap-2 rounded-lg bg-[#E2B93B] px-4 py-3 text-white">
        <AlertTriangle size={20} className="flex-shrink-0" />
        <p className="text-xs">
          Todas as postagens são propriedade desta plataforma. Qualquer
          modificação indevida compromete a integridade e confiabilidade das
          informações.
        </p>
      </div>

      <div className="space-y-4 font-semibold">
        <p>Baixar</p>

        <ImageTypeSelect onClick={handleDownload} options={options} />

        <Button
          className="w-full rounded-full  text-white"
          onClick={() => handleDownload('all')}
        >
          Todo o material
        </Button>
      </div>
    </div>
  )
}
